// src/components/mdx/client/ComparisonSlider.client.tsx

'use client'

import { type ReactNode, useState, useRef, useEffect } from 'react'
import { logClientError } from '~/lib/logger-client'
import { motion, useMotionValue, useTransform, animate } from "motion/react"
import Image from 'next/image'
import { cn } from '~/lib/utils'
import { InteractiveWrapper } from '~/components/blog/content/InteractiveWrapper'

/**
 * Props for the basic comparison slider component.
 * 
 * @interface ComparisonSliderProps
 * @property {string} beforeImage - URL of the "before" image to display
 * @property {string} afterImage - URL of the "after" image to display
 * @property {string} [beforeLabel='Before'] - Label text for the before image
 * @property {string} [afterLabel='After'] - Label text for the after image
 * @property {string} [className] - Additional CSS classes to apply
 * @property {number} [width=1200] - Image width for optimization
 * @property {number} [height=675] - Image height for optimization
 */
export interface ComparisonSliderProps {
  beforeImage: string
  afterImage: string
  beforeLabel?: string
  afterLabel?: string
  className?: string
  width?: number
  height?: number
}

/**
 * Props for the enhanced comparison slider with additional features.
 * 
 * @interface EnhancedComparisonSliderProps
 * @extends {ComparisonSliderProps}
 * @property {'video' | 'square' | 'portrait' | 'custom'} [aspectRatio='video'] - Aspect ratio preset
 * @property {string} [customAspectRatio] - Custom aspect ratio class when aspectRatio is 'custom'
 * @property {number} [initialPosition=50] - Initial slider position as percentage (0-100)
 * @property {ReactNode} [overlay] - Optional overlay content to display on top
 * @property {string} [caption] - Caption text to display below the slider
 * @property {Object} [metrics] - Performance metrics to display
 *   @property {string} [metrics.improvement] - Improvement percentage text
 *   @property {string} [metrics.engagementIncrease] - Engagement increase text
 *   @property {string} [metrics.conversionLift] - Conversion lift text
 */
export interface EnhancedComparisonSliderProps extends ComparisonSliderProps {
  aspectRatio?: 'video' | 'square' | 'portrait' | 'custom'
  customAspectRatio?: string
  initialPosition?: number
  overlay?: ReactNode
  caption?: string
  metrics?: {
    improvement?: string
    engagementIncrease?: string
    conversionLift?: string
  }
}

/**
 * Simple comparison slider with basic functionality for before/after image comparisons.
 * Features drag interaction and smooth animations with pointer events.
 * 
 * @param {ComparisonSliderProps} props - Component props
 * @returns {JSX.Element} The rendered comparison slider component
 * @example
 * <ComparisonSlider
 *   beforeImage="/images/before.jpg"
 *   afterImage="/images/after.jpg"
 *   beforeLabel="Original Design"
 *   afterLabel="Improved Design"
 * />
 */
export function ComparisonSliderClient({
  beforeImage,
  afterImage,
  beforeLabel = 'Before',
  afterLabel = 'After',
  width = 1200,
  height = 675,
  className,
}: ComparisonSliderProps): JSX.Element {
  // State to track when images have loaded for animation timing
  const [isLoaded, setIsLoaded] = useState(false)
  
  // Motion value for slider position (0-100 percentage)
  const x = useMotionValue(50)
  
  // Ref for the slider container to handle pointer events and constraints
  const sliderRef = useRef<HTMLDivElement>(null)
  
  // Track dragging state to prevent unwanted interactions
  const isDragging = useRef(false)

  // Transform slider position to CSS clip-path for revealing before image
  const clipPath = useTransform(
    x,
    [0, 100],
    ['inset(0 50% 0 0)', 'inset(0 0 0 0)']
  )

  /**
   * Handles pointer down events to start dragging interaction.
   * Sets up pointer capture for smooth dragging experience.
   */
  const handlePointerDown = (e: React.PointerEvent): void => {
    if (!sliderRef.current) return
    isDragging.current = true
    sliderRef.current.setPointerCapture(e.pointerId)
    void updateSliderPosition(e)
  }

  /**
   * Handles pointer move events during dragging.
   * Updates slider position and prevents default browser behavior.
   */
  const handlePointerMove = (e: React.PointerEvent): void => {
    if (!isDragging.current) return
    void updateSliderPosition(e)
    e.preventDefault()
  }

  /**
   * Handles pointer up events to end dragging interaction.
   * Releases pointer capture and resets dragging state.
   */
  const handlePointerUp = (e: React.PointerEvent): void => {
    if (!sliderRef.current) return
    isDragging.current = false
    sliderRef.current.releasePointerCapture(e.pointerId)
  }

  /**
   * Updates the slider position based on pointer coordinates.
   * Calculates percentage position and animates to new value with spring physics.
   */
  const updateSliderPosition = async (e: React.PointerEvent): Promise<void> => {
    const slider = sliderRef.current
    if (!slider) return

    // Calculate position as percentage of container width
    const rect = slider.getBoundingClientRect()
    const offsetX = e.clientX - rect.left
    const percentage = Math.min(Math.max((offsetX / rect.width) * 100, 0), 100)
    
    try {
      // Animate to new position with spring physics for smooth feel
      await animate(x, percentage, { type: 'spring', bounce: 0, duration: 0.2 })
    } catch (error) {
      logClientError(error, { component: 'ComparisonSlider', context: 'Animation failed' })
    }
  }

  // Animate slider to center position when images load
  useEffect(() => {
    const animateOnLoad = async (): Promise<void> => {
      if (isLoaded) {
        try {
          // Animate to center with bouncy spring for engaging entrance
          await animate(x, 50, { type: 'spring', bounce: 0.5, duration: 1 })
        } catch (error) {
          logClientError(error, { component: 'ComparisonSlider', context: 'Animation failed' })
        }
      }
    }
    void animateOnLoad()
  }, [isLoaded, x])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isLoaded ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className={cn('my-8 select-none', className)}
    >
      <div
        ref={sliderRef}
        className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {/* After Image (Background) - Always visible as base layer */}
        <Image
          src={afterImage}
          alt={afterLabel}
          width={width}
          height={height}
          className="object-cover"
          sizes="(min-width: 1280px) 1200px, (min-width: 780px) 800px, 100vw"
          onLoad={() => setIsLoaded(true)}
          priority
        />

        {/* Before Image (Foreground with clip) - Revealed by slider position */}
        <motion.div style={{ clipPath }} className="absolute inset-0">
          <Image
            src={beforeImage}
            alt={beforeLabel}
            width={width}
            height={height}
            className="object-cover"
            sizes="(min-width: 1280px) 1200px, (min-width: 780px) 800px, 100vw"
            priority
          />
        </motion.div>

        {/* Slider Handle - Draggable control with visual feedback */}
        <motion.div
          style={{ x: useTransform(x, [0, 100], ['0%', '100%']) }}
          className="absolute inset-y-0 left-0 z-10 flex w-0.5 cursor-ew-resize items-center justify-center"
          drag="x"
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={sliderRef}
          onDrag={(_, info) => {
            if (!sliderRef.current) return
            const rect = sliderRef.current.getBoundingClientRect()
            const percentage = Math.min(
              Math.max(((info.point.x - rect.left) / rect.width) * 100, 0),
              100
            )
            x.set(percentage)
          }}
        >
          {/* Handle Design - Circular button with drag arrows */}
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg ring-2 ring-gray-400/20">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-gray-600 to-gray-800">
              <svg
                className="h-4 w-4 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              >
                <path d="M 6 2 L 2 6 L 6 10" />
                <path d="M 18 2 L 22 6 L 18 10" />
              </svg>
            </div>
          </div>

          {/* Vertical Line - Visual separator between images */}
          <div className="absolute inset-y-0 w-0.5 bg-white/80">
            <div className="absolute inset-0 bg-gradient-to-b from-white/0 via-white to-white/0" />
          </div>
        </motion.div>

        {/* Labels - Dynamic opacity based on slider position */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-between p-4 text-white">
          <motion.span
            style={{ opacity: useTransform(x, [0, 30], [1, 0]) }}
            className="rounded-full bg-black/50 px-3 py-1.5 text-sm font-medium backdrop-blur-sm"
          >
            {beforeLabel}
          </motion.span>
          <motion.span
            style={{ opacity: useTransform(x, [70, 100], [0, 1]) }}
            className="rounded-full bg-black/50 px-3 py-1.5 text-sm font-medium backdrop-blur-sm"
          >
            {afterLabel}
          </motion.span>
        </div>
      </div>
    </motion.div>
  )
}

/**
 * Enhanced comparison slider with additional features and customization options.
 * Includes aspect ratio control, metrics display, overlays, and captions.
 * Wrapped in InteractiveWrapper for analytics tracking.
 * 
 * @param {EnhancedComparisonSliderProps} props - Component props
 * @returns {JSX.Element} The rendered enhanced comparison slider component
 * @example
 * <EnhancedComparisonSlider
 *   beforeImage="/images/before.jpg"
 *   afterImage="/images/after.jpg"
 *   aspectRatio="square"
 *   initialPosition={30}
 *   metrics={{
 *     improvement: "25%",
 *     engagementIncrease: "40%"
 *   }}
 *   caption="Performance improvements after optimization"
 * />
 */
export function EnhancedComparisonSliderClient({
  beforeImage,
  afterImage,
  beforeLabel = 'Before',
  afterLabel = 'After',
  width = 1200,
  height = 675,
  aspectRatio = 'video',
  customAspectRatio,
  initialPosition = 50,
  overlay,
  caption,
  metrics,
  className,
}: EnhancedComparisonSliderProps): JSX.Element {
  // State to track when images have loaded for animation timing
  const [isLoaded, setIsLoaded] = useState(false)
  
  // Motion value for slider position, starting at custom initial position
  const x = useMotionValue(initialPosition)
  
  // Ref for the slider container to handle pointer events and constraints
  const sliderRef = useRef<HTMLDivElement>(null)
  
  // Track dragging state to prevent unwanted interactions
  const isDragging = useRef(false)

  // Transform slider position to CSS clip-path for revealing before image
  const clipPath = useTransform(
    x,
    [0, 100],
    ['inset(0 50% 0 0)', 'inset(0 0 0 0)']
  )

  // Aspect ratio class mapping for different container shapes
  const aspectRatioClass = {
    video: 'aspect-[16/9]',
    square: 'aspect-[1/1]',
    portrait: 'aspect-[3/4]',
    custom: customAspectRatio,
  } as const

  /**
   * Handles pointer down events to start dragging interaction.
   * Sets up pointer capture for smooth dragging experience.
   */
  const handlePointerDown = (e: React.PointerEvent): void => {
    if (!sliderRef.current) return
    isDragging.current = true
    sliderRef.current.setPointerCapture(e.pointerId)
    void updateSliderPosition(e)
  }

  /**
   * Handles pointer move events during dragging.
   * Updates slider position and prevents default browser behavior.
   */
  const handlePointerMove = (e: React.PointerEvent): void => {
    if (!isDragging.current) return
    void updateSliderPosition(e)
    e.preventDefault()
  }

  /**
   * Handles pointer up events to end dragging interaction.
   * Releases pointer capture and resets dragging state.
   */
  const handlePointerUp = (e: React.PointerEvent): void => {
    if (!sliderRef.current) return
    isDragging.current = false
    sliderRef.current.releasePointerCapture(e.pointerId)
  }

  /**
   * Updates the slider position based on pointer coordinates.
   * Calculates percentage position and animates to new value with spring physics.
   */
  const updateSliderPosition = async (e: React.PointerEvent): Promise<void> => {
    const slider = sliderRef.current
    if (!slider) return

    // Calculate position as percentage of container width
    const rect = slider.getBoundingClientRect()
    const offsetX = e.clientX - rect.left
    const percentage = Math.min(Math.max((offsetX / rect.width) * 100, 0), 100)
    
    try {
      // Animate to new position with spring physics for smooth feel
      await animate(x, percentage, { type: 'spring', bounce: 0, duration: 0.2 })
    } catch (error) {
      logClientError(error, { component: 'ComparisonSlider', context: 'Animation failed' })
    }
  }

  // Animate slider to initial position when images load
  useEffect(() => {
    const animateOnLoad = async (): Promise<void> => {
      if (isLoaded) {
        try {
          // Animate to initial position with bouncy spring for engaging entrance
          await animate(x, initialPosition, { type: 'spring', bounce: 0.5, duration: 1 })
        } catch (error) {
          logClientError(error, { component: 'ComparisonSlider', context: 'Animation failed' })
        }
      }
    }
    void animateOnLoad()
  }, [isLoaded, initialPosition, x])

  return (
    <InteractiveWrapper>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className={cn('my-8 select-none', className)}
      >
        <div
          ref={sliderRef}
          className={cn(
            'relative w-full overflow-hidden rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900',
            aspectRatio === 'custom' ? customAspectRatio : aspectRatioClass[aspectRatio] || 'aspect-[16/9]'
          )}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {/* After Image (Background) - Always visible as base layer */}
          <Image
            src={afterImage}
            alt={afterLabel}
            width={width}
            height={height}
            className="object-cover"
            sizes="(min-width: 1280px) 1200px, (min-width: 780px) 800px, 100vw"
            onLoad={() => setIsLoaded(true)}
            priority
          />

          {/* Before Image (Foreground with clip) - Revealed by slider position */}
          <motion.div style={{ clipPath }} className="absolute inset-0">
            <Image
              src={beforeImage}
              alt={beforeLabel}
              width={width}
              height={height}
              className="object-cover"
              sizes="(min-width: 1280px) 1200px, (min-width: 780px) 800px, 100vw"
              priority
            />
          </motion.div>

          {/* Slider Handle - Draggable control with visual feedback */}
          <motion.div
            style={{ x: useTransform(x, [0, 100], ['0%', '100%']) }}
            className="absolute inset-y-0 left-0 z-10 flex w-0.5 cursor-ew-resize items-center justify-center"
            drag="x"
            dragMomentum={false}
            dragElastic={0}
            dragConstraints={sliderRef}
            onDrag={(_, info) => {
              if (!sliderRef.current) return
              const rect = sliderRef.current.getBoundingClientRect()
              const percentage = Math.min(
                Math.max(((info.point.x - rect.left) / rect.width) * 100, 0),
                100
              )
              x.set(percentage)
            }}
          >
            {/* Handle Design - Circular button with drag arrows */}
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg ring-2 ring-gray-400/20">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-gray-600 to-gray-800">
                <svg
                  className="h-4 w-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                >
                  <path d="M 6 2 L 2 6 L 6 10" />
                  <path d="M 18 2 L 22 6 L 18 10" />
                </svg>
              </div>
            </div>

            {/* Vertical Line - Visual separator between images */}
            <div className="absolute inset-y-0 w-0.5 bg-white/80">
              <div className="absolute inset-0 bg-gradient-to-b from-white/0 via-white to-white/0" />
            </div>
          </motion.div>

          {/* Labels - Dynamic opacity based on slider position */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-between p-4 text-white">
            <motion.span
              style={{ opacity: useTransform(x, [0, 30], [1, 0]) }}
              className="rounded-full bg-black/50 px-3 py-1.5 text-sm font-medium backdrop-blur-sm"
            >
              {beforeLabel}
            </motion.span>
            <motion.span
              style={{ opacity: useTransform(x, [70, 100], [0, 1]) }}
              className="rounded-full bg-black/50 px-3 py-1.5 text-sm font-medium backdrop-blur-sm"
            >
              {afterLabel}
            </motion.span>
          </div>

          {/* Optional Overlay - Custom content layer above images */}
          {overlay && <div className="absolute inset-0 z-20">{overlay}</div>}

          {/* Metrics Display - Animated performance indicators */}
          {metrics && (
            <div className="pointer-events-none absolute left-4 top-4 z-20 space-y-2">
              {metrics.improvement && (
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="block rounded-full bg-green-500/90 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-sm"
                >
                  {metrics.improvement} Improvement
                </motion.span>
              )}
              {metrics.engagementIncrease && (
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 }}
                  className="block rounded-full bg-blue-500/90 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-sm"
                >
                  {metrics.engagementIncrease} Engagement
                </motion.span>
              )}
              {metrics.conversionLift && (
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9 }}
                  className="block rounded-full bg-purple-500/90 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-sm"
                >
                  {metrics.conversionLift} Conversion
                </motion.span>
              )}
            </div>
          )}
        </div>

        {/* Caption - Optional descriptive text below the slider */}
        {caption && (
          <motion.figcaption
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-3 text-center text-sm text-gray-600 dark:text-gray-400"
          >
            {caption}
          </motion.figcaption>
        )}
      </motion.div>
    </InteractiveWrapper>
  )
}
